import { toast } from "sonner";
import React, { useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Turnstile, useTurnstileEnforced } from "../components/Turnstile";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Skeleton } from "../components/ui/skeleton";
import { Loader2, KeyRound, ArrowRight, ShieldCheck } from "lucide-react";
import { formatApiError } from "../lib/api";

export default function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const { setUserAfterLogin } = useAuth();

  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [cfToken, setCfToken] = useState("");
  const turnstileRef = useRef(null);
  const turnstileEnforced = useTurnstileEnforced();
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    if (!token) {
      setError("Missing invitation token in the URL.");
      setLoading(false);
      return;
    }
    api.get("/auth/invite-info", { params: { token } })
      .then(r => setInfo(r.data))
      .catch(e => setError(formatApiError(e?.response?.data?.detail) || "Invalid invitation"))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/accept-invite", { token, password, "cf-turnstile-response": cfToken || undefined });
      // Drop the access token into localStorage exactly like LoginPage does
      if (data.token) localStorage.setItem("epc_token", data.token);
      if (data.mfa_token) {
        toast.success("Password set — now sign in to set up two-step verification");
        navigate("/login", { replace: true, state: { email: data.email } });
        return;
      }
      if (setUserAfterLogin) setUserAfterLogin(data.user);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Failed to accept invitation");
    } finally {
      setSubmitting(false);
      turnstileRef.current?.reset();
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white" data-testid="accept-invite-page">
      <div className="flex flex-col p-8 lg:p-16">
        <div className="flex items-center gap-3 mb-16">
          <img src="/brand/housing-intelligence-logo.png" alt="Housing Intelligence" className="h-12 w-12 object-contain" />
          <div className="leading-tight">
            <div className="font-heading font-black text-lg" style={{ color: "var(--hi-olive)" }}>Housing Intelligence</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">EPC Register monitoring</div>
          </div>
        </div>

        <div className="flex-1 flex flex-col justify-center max-w-md">
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-3">SET YOUR PASSWORD</div>
          <h1 className="font-heading font-black text-4xl lg:text-5xl tracking-tight mb-3" style={{ color: "var(--hi-olive)" }}>
            Welcome aboard.
          </h1>

          {loading ? (
            <Skeleton className="h-32 mt-6" />
          ) : !info ? (
            <Alert variant="destructive" className="rounded-none mt-4" data-testid="invite-error">
              <AlertDescription>{error || "Invalid invitation"}</AlertDescription>
            </Alert>
          ) : (
            <>
              <div className="mt-2 mb-8 border border-slate-200 p-4" style={{ backgroundColor: "var(--hi-cream)" }} data-testid="invite-summary">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-muted-foreground"><ShieldCheck className="h-3.5 w-3.5" /> Account</div>
                <div className="mt-2 text-sm"><span className="text-muted-foreground">Email:</span> <span className="font-mono">{info.email}</span></div>
                <div className="text-sm"><span className="text-muted-foreground">Role:</span> <span className="font-semibold uppercase">{info.role}</span></div>
                <div className="text-sm"><span className="text-muted-foreground">Name:</span> {info.name || "—"}</div>
              </div>

              <form onSubmit={submit} className="space-y-5" data-testid="accept-invite-form">
                <div className="space-y-2">
                  <Label htmlFor="pw" className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">New password (min 6 chars)</Label>
                  <Input id="pw" type="password" minLength={6} required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="invite-password" className="rounded-none h-11 border-slate-300" placeholder="••••••••" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cf" className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Confirm password</Label>
                  <Input id="cf" type="password" minLength={6} required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="invite-confirm" className="rounded-none h-11 border-slate-300" placeholder="••••••••" />
                </div>

                {error && (
                  <Alert variant="destructive" className="rounded-none" data-testid="invite-submit-error">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <Turnstile ref={turnstileRef} action="invite" onToken={setCfToken} testId="invite-turnstile" />

                <Button type="submit" disabled={submitting || (turnstileEnforced && !cfToken)} className="w-full h-11 rounded-none text-white font-semibold group" style={{ backgroundColor: "var(--hi-olive)" }} data-testid="invite-submit">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><KeyRound className="h-4 w-4 mr-2" /> Set password & sign in <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" /></>)}
                </Button>
              </form>
            </>
          )}
        </div>

        <div className="mt-8 text-xs text-muted-foreground">© {new Date().getFullYear()} Housing Intelligence</div>
      </div>

      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1591955664169-4e2c269adecc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="UK housing" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(42,58,20,0.92) 0%, rgba(42,58,20,0.45) 50%, transparent 100%)" }} />
        <div className="absolute bottom-0 left-0 right-0 p-12 text-white">
          <div className="text-xs uppercase tracking-[0.25em] text-white/80 mb-4">YOU'VE BEEN INVITED</div>
          <div className="font-heading font-black text-3xl leading-tight max-w-md">
            Pick a password and start monitoring your portfolio in seconds.
          </div>
        </div>
      </div>
    </div>
  );
}
